/**
 * Gestor de stack de navegación
 * @module classes/navigation/StackManager
 */

import localforage from 'localforage';

/**
 * Clase para gestionar el stack de navegación
 * @class StackManager
 */
export class StackManager {
    /**
     * Constructor del StackManager
     */
    constructor() {
        this.maxStackSize = 5;
    }

    /**
     * Agrega una entrada al stack (args se clonan: no persisten claves que empiecen con `_`).
     * @async
     * @param {string} controllerName - Nombre del controlador
     * @param {Object|boolean} originalArgs - Argumentos del controlador
     * @param {number} scrollPosition - Posición de scroll
     */
    async addToStack(controllerName, originalArgs = false, scrollPosition = 0) {
        if (!controllerName) {
            return;
        }

        try {
            const stack = await localforage.getItem('appStack') || [];
            /** @type {Object|false} */
            let stackArgs = false;
            if (originalArgs && typeof originalArgs === 'object') {
                stackArgs = { ...originalArgs };
                for (const key of Object.keys(stackArgs)) {
                    if (key.startsWith('_')) {
                        delete stackArgs[key];
                    }
                }
                if (stackArgs.params && typeof stackArgs.params === 'object') {
                    stackArgs.params = { ...stackArgs.params };
                    for (const key of Object.keys(stackArgs.params)) {
                        if (key.startsWith('_')) {
                            delete stackArgs.params[key];
                        }
                    }
                    if (Object.keys(stackArgs.params).length === 0) {
                        delete stackArgs.params;
                    }
                }
            }

            if (stack.length > 0 && stack[stack.length - 1].controllerName === controllerName) {
                stack[stack.length - 1] = { controllerName, args: stackArgs, scrollPosition };
            } else {
                stack.push({ controllerName, args: stackArgs, scrollPosition });
                if (stack.length > this.maxStackSize) {
                    stack.splice(0, stack.length - this.maxStackSize);
                }
            }
            await localforage.setItem('appStack', stack);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Remueve la última entrada del stack
     * @async
     */
    async removeFromStack() {
        try {
            const stack = await localforage.getItem('appStack') || [];
            stack.pop();
            await localforage.setItem('appStack', stack);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Establece la posición de scroll para el controlador actual
     * @async
     * @param {string} currentControllerName - Nombre del controlador actual
     * @param {number} scrollPosition - Posición de scroll
     */
    async setScrollPosition(currentControllerName, scrollPosition) {
        if (!currentControllerName) return;

        try {
            const stack = await localforage.getItem('appStack') || [];

            if (stack.length > 0 && stack[stack.length - 1].controllerName === currentControllerName) {
                stack[stack.length - 1].scrollPosition = scrollPosition;
                await localforage.setItem('appStack', stack);
            }
        } catch (error) {
            throw error;
        }
    }

    /**
     * Establece el estado de elementos guardados
     * @async
     * @param {string} currentControllerName - Nombre del controlador actual
     * @param {Object} elementsStatus - Estado de los elementos
     */
    async setSavedStatusElements(currentControllerName, elementsStatus) {
        if (!currentControllerName || !elementsStatus) return;

        try {
            const stack = await localforage.getItem('appStack') || [];

            if (stack.length > 0 && stack[stack.length - 1].controllerName === currentControllerName) {
                if (!stack[stack.length - 1].args) {
                    stack[stack.length - 1].args = {};
                }
                stack[stack.length - 1].args.elementsStatus = elementsStatus;
                await localforage.setItem('appStack', stack);
            }
        } catch (error) {
            throw error;
        }
    }

    /**
     * Obtiene la última página restaurable
     * @async
     * @returns {Promise<Object|null>} Última página restaurable o null
     */
    async getLastRestorablePage() {
        try {
            const stack = await localforage.getItem('appStack') || [];
            if (stack.length > 0) {
                for (let i = stack.length - 1; i >= 0; i--) {
                    const item = stack[i];
                    if (item && item.args && item.args.restorable) {
                        return item;
                    }
                }
            }
            return null;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Limpia el stack
     * @async
     */
    async clearStack() {
        try {
            await localforage.removeItem('appStack');
        } catch (error) {
            throw error;
        }
    }

    /**
     * Obtiene el stack completo
     * @async
     * @returns {Promise<Array>} Stack completo
     */
    async getStack() {
        try {
            return await localforage.getItem('appStack') || [];
        } catch (error) {
            throw error;
        }
    }

    /**
     * Guarda el estado de la página actual antes de cambiar a otra
     * @async
     * @param {HTMLElement} oldPage - Elemento de la página anterior
     * @param {Function} getSavedStatusElements - Función para obtener elementos guardados
     * @returns {Promise<HTMLElement|null>} Elemento de la página anterior o null
     */
    async saveToStack(oldPage, getSavedStatusElements) {
        if (!oldPage) {
            return null;
        }

        const isRestorable = oldPage.getAttribute('data-restorable');
        if (isRestorable) {
            const currentControllerName = oldPage.getAttribute('data-controller');
            if (currentControllerName) {
                const content = document.querySelector('.app-content');
                const savedScrollPosition = content ? content.scrollTop : 0;
                await this.setScrollPosition(currentControllerName, savedScrollPosition);
                await this.setSavedStatusElements(currentControllerName, getSavedStatusElements(oldPage));
            }
        }

        return oldPage;
    }
}

